import React, { useEffect, useState } from "react";
import { LuChevronDown } from "react-icons/lu";
import { db } from "../utils/firebase";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { toast } from "react-toastify";

const PAGE_SIZE = 20;

const STATUS_STYLE = {
  Processing: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-900/50",
  "In Review": "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-900/50",
  Completed:  "bg-green-50 text-green-700 border-green-200 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900/50",
  Cancelled:  "bg-red-50 text-red-600 border-red-200 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900/50",
};

const STATUSES = ["Processing", "In Review", "Completed", "Cancelled"];

function OrdersTable() {
  const [orders,          setOrders]          = useState([]);
  const [usersMap,        setUsersMap]        = useState({});
  const [selectedStatus,  setSelectedStatus]  = useState("All");
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [page,            setPage]            = useState(1);

  const fetchOrders = async () => {
    const snap = await getDocs(collection(db, "orders"));
    setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchUsers = async () => {
    const snap = await getDocs(collection(db, "users"));
    const map = {};
    snap.forEach((d) => { map[d.id] = d.data(); });
    setUsersMap(map);
  };

  useEffect(() => {
    (async () => {
      await Promise.all([fetchOrders(), fetchUsers()]);
      setLoading(false);
    })();
  }, []);

  // Reset to first page whenever status filter changes
  useEffect(() => setPage(1), [selectedStatus]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateDoc(doc(db, "orders", orderId), { status: newStatus });
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status: newStatus } : o));
      toast.success(`Order status updated to "${newStatus}"`);
    } catch {
      toast.error("Failed to update order status. Please try again.");
    }
  };

  const filteredOrders = selectedStatus === "All"
    ? orders
    : orders.filter((o) => o.status === selectedStatus);

  const totalPages    = Math.max(1, Math.ceil(filteredOrders.length / PAGE_SIZE));
  const paginatedOrders = filteredOrders.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="animate-pulse bg-muted rounded-2xl h-16" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-2">
        {["All", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setSelectedStatus(s)}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${
              selectedStatus === s
                ? "bg-brand-navy text-white"
                : "bg-card border border-border text-[var(--color-muted-foreground)] hover:border-accent hover:text-accent"
            }`}
          >
            {s}
          </button>
        ))}
        <span className="ml-auto text-xs text-[var(--color-muted-foreground)] self-center font-medium">
          {filteredOrders.length} order{filteredOrders.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Orders list */}
      {paginatedOrders.length === 0 ? (
        <div className="bg-card rounded-2xl border border-border p-12 text-center text-[var(--color-muted-foreground)] text-sm">
          No orders found.
        </div>
      ) : (
        <div className="space-y-3">
          {paginatedOrders.map((order) => {
            const userId      = order.userId || order.shippingAddress?.id;
            const user        = usersMap[userId];
            const displayName = user?.name || "Unknown";
            const isExpanded  = expandedOrderId === order.id;
            const statusCls   = STATUS_STYLE[order.status] || "bg-slate-50 text-slate-600 border-slate-200 dark:bg-white/5 dark:text-slate-300 dark:border-white/10";

            return (
              <div key={order.id} className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
                {/* Row */}
                <div className="grid grid-cols-12 items-center px-6 py-4 gap-4">
                  <div className="col-span-12 sm:col-span-3">
                    <p className="text-xs text-[var(--color-muted-foreground)] font-medium mb-0.5">Order ID</p>
                    <p className="font-black text-foreground font-mono text-sm">#{order.id.slice(-8).toUpperCase()}</p>
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <p className="text-xs text-[var(--color-muted-foreground)] font-medium mb-0.5">Customer</p>
                    <p className="font-semibold text-foreground text-sm truncate">{displayName}</p>
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <p className="text-xs text-[var(--color-muted-foreground)] font-medium mb-0.5">Total</p>
                    <p className="font-black text-foreground">${Number(order.total).toFixed(2)}</p>
                  </div>
                  <div className="col-span-8 sm:col-span-3">
                    <p className="text-xs text-[var(--color-muted-foreground)] font-medium mb-1">Status</p>
                    <select
                      value={order.status}
                      onChange={(e) => handleStatusChange(order.id, e.target.value)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full border cursor-pointer focus:outline-none ${statusCls}`}
                    >
                      {STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-span-4 sm:col-span-1 flex justify-end">
                    <button
                      onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                      className="w-9 h-9 rounded-xl border border-border flex items-center justify-center hover:bg-muted transition-colors"
                    >
                      <LuChevronDown size={16} className={`text-[var(--color-muted-foreground)] transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-border px-6 py-5 bg-muted dark:bg-[#0E1223]">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <p className="text-xs font-bold text-[var(--color-muted-foreground)] uppercase tracking-widest mb-3">Order Items</p>
                        <div className="space-y-2">
                          {order.items?.map((item, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span className="text-foreground font-medium">{item.name} <span className="text-[var(--color-muted-foreground)]">× {item.quantity}</span></span>
                              <span className="font-bold text-foreground">${Number(item.price).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 pt-3 border-t border-border flex justify-between text-sm">
                          <span className="font-bold text-[var(--color-muted-foreground)]">Total</span>
                          <span className="font-black text-foreground">${Number(order.total).toFixed(2)}</span>
                        </div>
                      </div>
                      {order.shippingAddress && (
                        <div>
                          <p className="text-xs font-bold text-[var(--color-muted-foreground)] uppercase tracking-widest mb-3">Ship To</p>
                          <p className="text-sm text-foreground">
                            {order.shippingAddress.street}, {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zip}
                          </p>
                          {order.createdAt && (
                            <p className="text-xs text-[var(--color-muted-foreground)] mt-2">
                              Placed {order.createdAt.toDate?.().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Page {page} of {totalPages} · {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredOrders.length)} of {filteredOrders.length} orders
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-xs font-bold rounded-xl border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              ← Prev
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 text-xs font-bold rounded-xl border border-border text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrdersTable;
